/**
 * FDR-facing Universe Portal — public route, no Core session.
 *
 * Polished for demo-grade impression:
 *   - Personalized hero ("Acme TPA — Memorial Health Plan is requesting 3 universes")
 *   - Soft entrance animation on the whole page (200ms ease-out)
 *   - Generous drop zone with contextual hint per universe type
 *   - Hero counts panel — large numbers, plain-English on hover
 *   - Findings as elevated cards with plain-English titles + animated expand
 *   - Confident success state when verdict.ok flips green
 *   - Subtle "powered by PCG" conversion hook
 *
 * SOC 2 unchanged: token + MFA flow identical, encryption identical, no new
 * data surfaces, no plan-confidential fields exposed beyond what the FDR
 * already needs to see.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';

const API = process.env.REACT_APP_API_URL || '';

function tokenFromUrl() {
  const m = window.location.pathname.match(/\/universe-portal\/([^/?#]+)/);
  return m ? m[1] : '';
}

export default function UniversePortal() {
  const token = useMemo(tokenFromUrl, []);
  const [stage, setStage] = useState('loading');
  const [error, setError] = useState(null);
  const [request, setRequest] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState('');
  const [mfaPass, setMfaPass] = useState(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setMounted(true));
    if (!token) { setStage('verify_failed'); setError('Missing portal link.'); return; }
    fetch(`${API}/api/universe-portal/verify`, {
      method: 'POST', headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(async r => {
        const data = await r.json();
        if (!r.ok) { setStage('verify_failed'); setError(data.error || 'Could not open link.'); return; }
        setRequest(data.request);
        setStage('request');
      })
      .catch(err => { setStage('verify_failed'); setError(err.message); });
  }, [token]);

  async function requestCode() {
    setError(null);
    try {
      const res = await fetch(`${API}/api/universe-portal/mfa/challenge`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not send code.');
      setChallenge(data.challenge);
      setStage('mfa_sent');
    } catch (e) { setError(e.message); }
  }

  async function verifyCode() {
    setError(null);
    try {
      const res = await fetch(`${API}/api/universe-portal/mfa/verify`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ challenge_id: challenge.challenge_id, code }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Code incorrect.');
      setMfaPass(data.pass_token);
      setStage('uploading');
    } catch (e) { setError(e.message); }
  }

  function reloadRequest() {
    return fetch(`${API}/api/universe-portal/verify`, {
      method: 'POST', headers: { 'Authorization': `Bearer ${token}` },
    }).then(r => r.json()).then(d => setRequest(d.request));
  }

  if (stage === 'loading') {
    return <Layout mounted={mounted}><div className="text-slate-500 text-sm">Opening secure portal…</div></Layout>;
  }

  if (stage === 'verify_failed') return (
    <Layout mounted={mounted}>
      <CenterCard>
        <div className="text-xs font-mono uppercase tracking-widest text-red-700 mb-2">Can't open link</div>
        <h2 className="text-lg font-semibold text-slate-900 mb-2">This portal link can't be opened</h2>
        <p className="text-sm text-slate-700">{error || 'Token invalid, expired, or already used.'}</p>
        <p className="text-xs text-slate-500 mt-4">Please ask the compliance team that sent the original message for a fresh link.</p>
      </CenterCard>
    </Layout>
  );

  return (
    <Layout mounted={mounted}>
      <Hero request={request}/>
      <ProgressStrip stage={stage}/>
      {stage === 'request' && <RequestCodeCard onRequest={requestCode} request={request}/>}
      {stage === 'mfa_sent' && <EnterCodeCard challenge={challenge} code={code} setCode={setCode} onVerify={verifyCode} onResend={requestCode} error={error}/>}
      {(stage === 'uploading' || stage === 'done') && (
        <ItemUploads request={request} token={token} mfaPass={mfaPass} reload={reloadRequest}/>
      )}
      {error && stage !== 'mfa_sent' && (
        <div className="mt-4 border border-red-200 bg-red-50 text-red-800 text-sm px-3 py-2 transition-opacity duration-200">{error}</div>
      )}
      <ConversionHook/>
    </Layout>
  );
}

function Layout({ children, mounted }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-baseline gap-3">
            <span className="text-xs font-mono uppercase tracking-widest text-red-700">PCG</span>
            <span className="text-sm font-semibold text-slate-900">Universe Portal</span>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 flex items-center gap-3">
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
              Encrypted
            </span>
            <span>·</span>
            <span>MFA</span>
            <span>·</span>
            <span>Audit-logged</span>
          </div>
        </div>
      </header>
      <main className={`max-w-4xl mx-auto px-6 py-10 transition-all duration-500 ease-out ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
        {children}
      </main>
    </div>
  );
}

function CenterCard({ children }) {
  return (
    <div className="min-h-[50vh] flex items-center justify-center">
      <div className="bg-white border border-slate-200 px-8 py-7 max-w-md w-full shadow-sm">{children}</div>
    </div>
  );
}

function Hero({ request }) {
  if (!request) return null;
  const itemCount = request.items?.length || 0;
  const plan = request.organization_name || 'A Medicare Advantage plan';
  return (
    <section className="mb-8">
      <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">For {request.fdr_name}</div>
      <h1 className="text-2xl font-semibold text-slate-900 leading-tight">
        <span className="text-red-700">{plan}</span> has requested {itemCount} {itemCount === 1 ? 'universe' : 'universes'}.
      </h1>
      <div className="text-sm text-slate-600 mt-2 leading-relaxed">
        {request.title}
        {request.reporting_period_start && (
          <span className="text-slate-500"> · period <span className="font-mono text-xs">{request.reporting_period_start} → {request.reporting_period_end}</span></span>
        )}
      </div>
      {request.message_to_fdr && (
        <div className="border-l-2 border-red-700 pl-3 mt-4 text-sm text-slate-700 italic">"{request.message_to_fdr}"</div>
      )}
      <div className="text-xs text-slate-500 mt-3">Link expires {new Date(request.expires_at).toLocaleString()}</div>
    </section>
  );
}

function ProgressStrip({ stage }) {
  const steps = [
    { id: 'request',   label: 'Verify identity' },
    { id: 'mfa_sent',  label: 'Enter code' },
    { id: 'uploading', label: 'Upload & validate' },
  ];
  const activeIdx = stage === 'request' ? 0 : stage === 'mfa_sent' ? 1 : 2;
  return (
    <ol className="flex gap-1 mb-8">
      {steps.map((s, i) => {
        const done = i < activeIdx;
        const active = i === activeIdx;
        return (
          <li key={s.id} className={`flex-1 flex items-center gap-2 px-3 py-2 border transition-all duration-300
            ${done   ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
            : active ? 'border-red-700 bg-red-50 text-red-800'
                     : 'border-slate-200 bg-white text-slate-500'}`}>
            <span className={`w-5 h-5 flex items-center justify-center text-[10px] font-mono ${
              done   ? 'bg-emerald-600 text-white' :
              active ? 'bg-red-700 text-white' :
                       'bg-slate-200 text-slate-600'
            } rounded-full transition-colors duration-300`}>
              {done ? '✓' : i + 1}
            </span>
            <span className="text-xs font-medium tracking-tight">{s.label}</span>
          </li>
        );
      })}
    </ol>
  );
}

function RequestCodeCard({ onRequest, request }) {
  return (
    <div className="bg-white border border-slate-200 p-7 shadow-sm">
      <h3 className="text-base font-semibold text-slate-900 mb-2">Verify it's you</h3>
      <p className="text-sm text-slate-700 leading-relaxed mb-5">
        We'll send a 6-digit code to <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{request.fdr_email_masked}</span>. It expires in 10 minutes.
      </p>
      <button onClick={onRequest}
        className="bg-red-700 hover:bg-red-800 active:bg-red-900 text-white px-5 py-2.5 text-sm font-semibold transition-all duration-200 hover:shadow-md">
        Send verification code →
      </button>
    </div>
  );
}

function EnterCodeCard({ challenge, code, setCode, onVerify, onResend, error }) {
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  return (
    <div className="bg-white border border-slate-200 p-7 shadow-sm">
      <h3 className="text-base font-semibold text-slate-900 mb-2">Enter the code</h3>
      <p className="text-sm text-slate-700 leading-relaxed mb-5">
        We sent a 6-digit code to <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{challenge.recipient_masked}</span>. It expires in 10 minutes.
      </p>
      <input
        ref={inputRef}
        type="text" inputMode="numeric" pattern="\d*" maxLength={6}
        value={code}
        onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        onKeyDown={e => { if (e.key === 'Enter' && code.length === 6) onVerify(); }}
        placeholder="000000"
        className="border-2 border-slate-300 focus:border-red-700 outline-none px-4 py-3 text-2xl font-mono tracking-[0.5em] w-60 mb-4 transition-colors duration-200"
      />
      <div className="flex gap-3 items-center">
        <button onClick={onVerify} disabled={code.length !== 6}
          className="bg-red-700 hover:bg-red-800 disabled:bg-slate-300 text-white px-5 py-2.5 text-sm font-semibold transition-all duration-200 hover:shadow-md disabled:hover:shadow-none">
          Verify code →
        </button>
        <button onClick={onResend} className="text-xs font-mono uppercase tracking-widest text-slate-500 hover:text-slate-900 transition-colors">
          Resend
        </button>
      </div>
      {error && <div className="mt-3 text-sm text-red-700 transition-opacity duration-200">{error}</div>}
    </div>
  );
}

function ItemUploads({ request, token, mfaPass, reload }) {
  const completed = (request.items || []).filter(i => i.status === 'accepted').length;
  const total = request.items?.length || 0;
  const allDone = total > 0 && completed === total;
  return (
    <div>
      <div className="flex items-baseline justify-between mb-5">
        <div>
          <h3 className="text-base font-semibold text-slate-900">Upload your files</h3>
          <p className="text-sm text-slate-600 leading-relaxed mt-0.5">
            Drop each file in its card. We validate the moment it lands — you'll see the same findings the compliance team would see.
          </p>
        </div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
          {completed} / {total} finalized
        </div>
      </div>
      <div className="space-y-3">
        {(request.items || []).map(it => (
          <ItemCard key={it.id} item={it} request={request} token={token} mfaPass={mfaPass} reload={reload}/>
        ))}
      </div>
      {allDone && <AllDoneCelebration/>}
      {!allDone && <SentAnotherWay request={request} token={token} mfaPass={mfaPass}/>}
    </div>
  );
}

// The FDR already sent the universe another way (SFTP, a prior upload). The
// statement pauses the plan's reminders and asks compliance to confirm; the
// request completes only when compliance marks the universes received.
function SentAnotherWay({ request, token, mfaPass }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(request?.acknowledged_at ? { acknowledged_at: request.acknowledged_at, acknowledged_by: request.acknowledged_by } : null);

  if (done) {
    return (
      <div className="mt-5 border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
        <span className="font-semibold">Reported as submitted</span>
        {done.acknowledged_by ? <> by {done.acknowledged_by}</> : null}
        {done.acknowledged_at ? <> on {new Date(done.acknowledged_at).toLocaleDateString()}</> : null}. The compliance team will confirm on their side; their reminders are paused.
      </div>
    );
  }

  async function submit() {
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${API}/api/universe-portal/${request.id}/acknowledge`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'x-mfa-pass': mfaPass, 'Content-Type': 'application/json' },
        body: JSON.stringify({ submitter_email: email.trim() || null, note: note.trim() || null }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not record the statement.');
      setDone({ acknowledged_at: data.acknowledged_at, acknowledged_by: data.acknowledged_by });
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="mt-6 border-t border-slate-200 pt-4">
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} className="text-sm text-slate-600 underline hover:text-slate-900">
          Already sent these universes another way? Tell the compliance team.
        </button>
      ) : (
        <div className="border border-slate-200 bg-slate-50 px-4 py-4 max-w-xl">
          <div className="text-sm font-semibold text-slate-900">Report the universes as already submitted</div>
          <p className="text-xs text-slate-600 mt-1">
            Tell the compliance team how and when they went (for example SFTP on the 3rd). Their reminders pause while they confirm. Do not include member information.
          </p>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value.slice(0, 200))} placeholder="Your email"
            className="mt-3 w-full border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:border-slate-500" />
          <textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 500))} rows={2} maxLength={500}
            placeholder="How and when the universes were submitted"
            className="mt-2 w-full border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:border-slate-500" />
          {error && <div className="text-xs text-red-700 mt-2">{error}</div>}
          <div className="flex items-center gap-3 mt-3">
            <button type="button" disabled={busy} onClick={submit}
              className="bg-slate-900 text-white px-4 py-1.5 text-sm font-semibold disabled:bg-slate-300">
              {busy ? 'Recording…' : 'Yes, they have been submitted'}
            </button>
            <button type="button" disabled={busy} onClick={() => setOpen(false)} className="text-sm text-slate-600 hover:text-slate-900">Not now</button>
          </div>
        </div>
      )}
    </div>
  );
}

function AllDoneCelebration() {
  return (
    <div className="mt-6 border-2 border-emerald-500 bg-emerald-50 px-5 py-6 text-center">
      <div className="text-3xl mb-2">✓</div>
      <div className="text-base font-semibold text-emerald-900">Everything's finalized.</div>
      <div className="text-sm text-emerald-800 mt-1">The compliance team has been notified. You can close this tab.</div>
    </div>
  );
}

function ItemCard({ item, request, token, mfaPass, reload }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [report, setReport] = useState(null);
  const [lastUpload, setLastUpload] = useState(null);
  const [error, setError] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const isAccepted = item.status === 'accepted';
  const isReceived = item.status === 'received';

  async function upload() {
    if (!file) return;
    setUploading(true); setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('item_id', item.id);
      const res = await fetch(`${API}/api/universe-portal/${request.id}/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'x-mfa-pass': mfaPass },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed.');
      setLastUpload(data);
      setReport(data.validation || null);
      setShowDetails(true);
      await reload();
    } catch (e) { setError(e.message); }
    finally { setUploading(false); }
  }

  async function finalize() {
    if (!lastUpload?.submission_id) return;
    setError(null);
    try {
      const res = await fetch(`${API}/api/universe-portal/${request.id}/finalize`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'x-mfa-pass': mfaPass, 'Content-Type': 'application/json' },
        body: JSON.stringify({ submission_id: lastUpload.submission_id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Finalize failed.');
      await reload();
    } catch (e) { setError(e.message); }
  }

  const consumerKind = item.consumer_app === 'scrubber' ? 'AUDIT' : 'REPORTING';
  const consumerHint = item.consumer_app === 'scrubber'
    ? 'This file routes to Audit Scrubber for the program audit.'
    : 'This file routes to Reporter for quarterly / annual reporting.';
  const accentLeft =
    isAccepted ? 'border-l-4 border-emerald-500' :
    isReceived ? 'border-l-4 border-amber-500' :
                 'border-l-4 border-transparent';

  return (
    <div className={`bg-white border border-slate-200 transition-all duration-300 ${accentLeft} ${isAccepted ? 'shadow-sm shadow-emerald-100' : ''}`}>
      <div className="px-5 py-4 flex items-start gap-4">
        <div className="flex-shrink-0">
          <div className={`text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-1 border ${
            item.consumer_app === 'scrubber' ? 'border-sky-400 text-sky-700 bg-sky-50' : 'border-red-700 text-red-700 bg-red-50'
          }`}>{consumerKind}</div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900 leading-snug">{item.catalog_label}</div>
          <div className="text-xs text-slate-500 mt-1">{consumerHint}</div>
        </div>
        <div>
          <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border transition-colors duration-300 ${
            isAccepted ? 'border-emerald-400 text-emerald-700 bg-emerald-50' :
            isReceived ? 'border-amber-500 text-amber-700 bg-amber-50' :
                         'border-slate-300 text-slate-500 bg-slate-50'
          }`}>{item.status}</span>
        </div>
      </div>

      {!isAccepted && (
        <div className="px-5 pb-5 border-t border-slate-100 pt-4">
          <FileDrop file={file} onChange={setFile} disabled={uploading} consumer={item.consumer_app} label={item.catalog_label}/>
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <button onClick={upload} disabled={!file || uploading}
              className="bg-red-700 hover:bg-red-800 active:bg-red-900 disabled:bg-slate-300 text-white px-5 py-2 text-sm font-semibold transition-all duration-200 hover:shadow-md disabled:hover:shadow-none">
              {uploading ? 'Validating…' : (lastUpload ? 'Upload another attempt' : 'Upload & validate')}
            </button>
            {lastUpload?.submission_id && report?.ok && (
              <button onClick={finalize}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2 text-sm font-semibold transition-all duration-200 hover:shadow-md">
                Mark final ✓
              </button>
            )}
            {lastUpload?.submission_id && !report?.ok && (
              <button onClick={finalize}
                className="border border-slate-400 text-slate-700 hover:bg-slate-100 px-5 py-2 text-sm font-semibold transition-all duration-200">
                Mark final anyway
              </button>
            )}
            {lastUpload && (
              <span className="text-[10px] font-mono text-slate-500 ml-auto">
                attempt {lastUpload.attempt_number} of 3 · sha256 {lastUpload.file_hash?.slice(0, 10)}… · {Math.round(lastUpload.file_size / 1024)} KB
              </span>
            )}
          </div>
          {error && <div className="mt-3 text-xs text-red-700">{error}</div>}
        </div>
      )}

      {report && (
        <ValidationReport report={report} expanded={showDetails} onToggle={() => setShowDetails(!showDetails)}/>
      )}
    </div>
  );
}

function FileDrop({ file, onChange, disabled, consumer, label }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  function onDrop(e) {
    e.preventDefault(); setDragOver(false);
    if (disabled) return;
    const f = e.dataTransfer.files?.[0];
    if (f) onChange(f);
  }

  const hint = consumer === 'scrubber'
    ? `${label} · CMS ODAG/CDAG layout · .xlsx or .csv`
    : `${label} · .xlsx, .csv, or HPMS .txt`;

  return (
    <div
      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      onClick={() => !disabled && inputRef.current?.click()}
      className={`relative border-2 border-dashed cursor-pointer px-5 py-6 transition-all duration-200 ${
        dragOver ? 'border-red-700 bg-red-50 scale-[1.01]' :
        file     ? 'border-emerald-400 bg-emerald-50/50' :
                   'border-slate-300 bg-slate-50/50 hover:border-red-700 hover:bg-red-50/30'
      }`}
    >
      <input ref={inputRef} type="file" className="hidden" onChange={e => onChange(e.target.files?.[0] || null)} disabled={disabled}/>
      <div className="flex items-center gap-4">
        <div className={`w-10 h-10 flex items-center justify-center transition-colors ${file ? 'text-emerald-700' : 'text-slate-400'}`}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-9 h-9">
            {file
              ? <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              : <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"/>}
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          {file ? (
            <>
              <div className="font-mono text-sm text-slate-900 truncate">{file.name}</div>
              <div className="text-xs text-slate-600 mt-0.5">{Math.round(file.size / 1024)} KB · ready to validate</div>
            </>
          ) : (
            <>
              <div className="text-sm font-medium text-slate-900">Drop your file here, or click to pick</div>
              <div className="text-xs text-slate-500 mt-0.5">{hint}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Validation report (Linear-style elevated cards) ───────────────────────
function ValidationReport({ report, expanded, onToggle }) {
  const { ok, verdict, summary, findings = [], warnings = [], counts } = report;
  const groups = useMemo(() => groupFindings(findings), [findings]);

  return (
    <div className={`border-t border-slate-200 transition-colors duration-300 ${ok ? 'bg-emerald-50/40' : 'bg-slate-50/70'}`}>
      <div className="px-5 py-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${ok ? 'bg-emerald-500 animate-pulse' : 'bg-red-700'}`} style={ok ? { animationDuration: '2.4s' } : {}}/>
          <div className="min-w-0">
            <div className={`text-sm font-semibold leading-tight ${ok ? 'text-emerald-900' : 'text-slate-900'}`}>
              {verdict?.label}
            </div>
            <div className="text-[11px] font-mono text-slate-600 mt-0.5">
              {summary?.critical || 0} critical · {summary?.high || 0} warnings · {summary?.totalRows ?? '—'} rows · {summary?.totalChecksRun || 0} checks
            </div>
          </div>
        </div>
        <button onClick={onToggle}
          className="text-[10px] font-mono uppercase tracking-widest text-slate-500 hover:text-slate-900 transition-colors flex-shrink-0">
          {expanded ? 'Hide details' : 'See details →'}
        </button>
      </div>

      <div className={`overflow-hidden transition-all duration-300 ease-out ${expanded ? 'max-h-[4000px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="px-5 pb-5 space-y-3">
          {counts && Object.keys(counts).length > 0 && <CountsHero counts={counts}/>}
          {groups.length === 0 ? (
            <div className="bg-white border border-emerald-200 px-4 py-5 text-center">
              <div className="text-emerald-700 text-xl mb-1">✓</div>
              <div className="text-sm font-medium text-emerald-900">No findings — the file is structurally clean.</div>
            </div>
          ) : (
            groups.map((g, i) => <FindingGroup key={g.key} group={g} delay={i * 30}/>)
          )}
          {warnings?.length > 0 && (
            <div className="border-l-2 border-slate-300 bg-slate-100 px-4 py-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">Notes</div>
              <ul className="text-xs text-slate-700 list-disc pl-4 space-y-1">
                {warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CountsHero({ counts }) {
  const entries = Object.entries(counts).filter(([, v]) => typeof v === 'number');
  if (entries.length === 0) return null;
  return (
    <div className="bg-white border border-slate-200">
      <div className="px-4 py-2.5 border-b border-slate-100">
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Counts derived from your file</div>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 divide-x divide-slate-100">
        {entries.map(([eid, val]) => (
          <div key={eid} className="px-3 py-3 text-center hover:bg-slate-50 transition-colors group cursor-default" title={CMS_ELEMENT_HINTS[eid] || ''}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Element {eid}</div>
            <div className="font-mono text-xl text-slate-900 mt-0.5 tabular-nums group-hover:text-red-700 transition-colors">{val.toLocaleString()}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FindingGroup({ group, delay = 0 }) {
  const [open, setOpen] = useState(group.severity === 'critical');
  const sevColors =
    group.severity === 'critical' ? { border: 'border-red-700', text: 'text-red-700', bg: 'bg-red-50' } :
    group.severity === 'high'     ? { border: 'border-amber-600', text: 'text-amber-700', bg: 'bg-amber-50' } :
                                    { border: 'border-slate-300', text: 'text-slate-600', bg: 'bg-slate-50' };
  return (
    <div
      className={`bg-white border border-slate-200 border-l-4 ${sevColors.border} shadow-sm transition-all duration-200 hover:shadow-md`}
      style={{ animation: `slideIn 240ms ease-out ${delay}ms both` }}
    >
      <style>{`@keyframes slideIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }`}</style>
      <button onClick={() => setOpen(!open)} className="w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-slate-50 transition-colors">
        <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${sevColors.border} ${sevColors.text} ${sevColors.bg} flex-shrink-0`}>{group.severity}</span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-900 leading-snug">{group.title}</div>
          <div className="text-[11px] font-mono text-slate-500 mt-0.5">{group.rule}</div>
        </div>
        <div className="text-xs text-slate-600 flex-shrink-0">
          {group.items.length} {group.items.length === 1 ? 'finding' : 'findings'}
        </div>
        <div className={`text-slate-400 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}>›</div>
      </button>
      <div className={`overflow-hidden transition-all duration-200 ease-out ${open ? 'max-h-[2400px]' : 'max-h-0'}`}>
        <ul className="divide-y divide-slate-100 border-t border-slate-100">
          {group.items.slice(0, 25).map((f, i) => (
            <li key={i} className="px-4 py-2.5">
              <div className="flex items-start gap-3 text-sm">
                <span className="text-[10px] font-mono text-slate-400 mt-0.5 w-14 flex-shrink-0">
                  {f.row ? `row ${f.row}` : f.line ? `line ${f.line}` : '—'}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-slate-900 leading-snug">{f.message}</div>
                  {(f.value !== undefined || f.expected) && (
                    <div className="text-[11px] font-mono text-slate-500 mt-1">
                      {f.field && <span>field <span className="text-slate-700 font-semibold">{f.field}</span> · </span>}
                      {f.value !== undefined && <span>got <span className="text-slate-700 font-semibold">"{String(f.value)}"</span></span>}
                      {f.expected && <span> · expected <span className="text-slate-700">{Array.isArray(f.expected) ? f.expected.join(', ') : String(f.expected)}</span></span>}
                    </div>
                  )}
                  {f.citation && <div className="text-[10px] font-mono text-slate-500 mt-1 italic">{f.citation}</div>}
                </div>
              </div>
            </li>
          ))}
          {group.items.length > 25 && (
            <li className="px-4 py-2 text-xs font-mono text-slate-500 bg-slate-50">
              …and {group.items.length - 25} more in this group.
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}

function ConversionHook() {
  return (
    <div className="mt-12 pt-6 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500 flex-wrap gap-3">
      <div>
        Built by <a href="https://precisioncompliancegroup.com" target="_blank" rel="noreferrer" className="text-red-700 hover:text-red-800 font-medium transition-colors">Precision Compliance Group</a>.
        Your plan uses this for audit + reporting workflows.
      </div>
      <a href="https://precisioncompliancegroup.com" target="_blank" rel="noreferrer"
         className="text-[10px] font-mono uppercase tracking-widest text-slate-500 hover:text-red-700 transition-colors">
        Want this for your plans? →
      </a>
    </div>
  );
}

function groupFindings(findings) {
  const map = new Map();
  for (const f of findings) {
    const key = `${f.severity}:${f.rule || 'finding'}`;
    if (!map.has(key)) {
      map.set(key, {
        key, severity: f.severity || 'high',
        rule: f.rule || 'finding',
        title: titleForRule(f.rule),
        items: [],
      });
    }
    map.get(key).items.push(f);
  }
  return [...map.values()].sort((a, b) => severityOrder(a.severity) - severityOrder(b.severity));
}
function severityOrder(s) { return s === 'critical' ? 0 : s === 'high' ? 1 : 2; }
function titleForRule(rule) {
  return ({
    header_mismatch:         'Header doesn\'t match the CMS layout',
    missing_required_field:  'Required field is empty',
    bad_coded_value:         'Value isn\'t in the CMS-approved set',
    bad_date_format:         'Date isn\'t in the format CMS expects',
    bad_date_month:          'Date month is out of range',
    bad_date_day:            'Date day is out of range',
    non_numeric:             'Value should be numeric',
    negative_value:          'Counts can\'t be negative',
    unexpected_decimals:     'CMS doesn\'t allow decimals on count fields',
    length_exceeds_max:      'Value is too long for the CMS-specified field width',
    length_below_min:        'Value is too short for the CMS-specified field width',
    bad_contract_format:     'Contract ID needs to match H/S/R/E + four digits',
    outside_reporting_period:'Row falls outside the requested reporting period',
    not_timely_notification: 'Notification wasn\'t delivered within the CMS timeframe',
    duplicate_mbi:           'Same beneficiary appears more than once',
    column_count_mismatch:   'Wrong number of columns for this section',
    file_empty:              'The file is empty',
    wrong_delimiter:         'Wrong delimiter — CMS requires tab',
    invariant_violation:     'Cross-element math doesn\'t add up',
    parse_error:             'We couldn\'t interpret the file',
    no_validator:            'This universe doesn\'t have a validator configured',
  })[rule] || (rule || 'finding').replace(/_/g, ' ');
}

const CMS_ELEMENT_HINTS = {
  A: 'Total — the headline count for this section',
  B: 'Of the total, those that met CMS timeliness',
  C: 'Expedited cases',
  D: 'Expedited cases that met timeliness',
  E: 'Dismissed / out-of-scope cases',
  F: 'Filer split — non-contract provider',
  G: 'Filer split — non-contract provider (claims)',
};
